/** Hover card showing blocked slot details, anchored to a calendar event element. */

import { Ban, Clock, NotebookPen, Stethoscope, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { type BlockedSlotWithStaff, type DateFormat, formatName } from '@snootsvet/shared'

import { FormattedGridTimeRange } from '@/frontend/components/formatted-datetime'
import { calcHoverCardPosition } from '@/frontend/features/appointments/appointment-calendar.helpers'

interface BlockedSlotHoverCardProps {
  block: BlockedSlotWithStaff
  anchorEl: HTMLElement
  /**
   * The calendar's own frame — the zone and clock the grid drew this block's slot
   * in. Not derived from `block.clinicId`: blocked slots are fetched by time range
   * and staff only, never filtered by clinic, so a block from another clinic (or
   * one with no clinic at all) can appear on this calendar.
   */
  fmt: DateFormat
}

export function BlockedSlotHoverCard({ block, anchorEl, fmt }: BlockedSlotHoverCardProps) {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    const tooltip = tooltipRef.current
    if (!tooltip) return
    setPosition(calcHoverCardPosition(anchorEl, tooltip))
  }, [anchorEl])

  const vetName = formatName(block.staff) || block.staff.email

  return createPortal(
    <div
      ref={tooltipRef}
      className="pointer-events-none fixed z-50 rounded-md border bg-popover px-3 py-2 shadow-md animate-in fade-in-0 zoom-in-95"
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        opacity: position ? 1 : 0,
      }}
    >
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5 font-semibold">
          <Ban className="size-3.5 text-muted-foreground" />
          Blocked
        </div>
        <div className="space-y-1 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Clock className="size-3 shrink-0" />
            <FormattedGridTimeRange start={block.startTime} end={block.endTime} fmt={fmt} />
          </div>
          <div className="flex items-center gap-1.5">
            <Stethoscope className="size-3 shrink-0" />
            {vetName}
          </div>
          {block.createdByStaff && (
            <div className="flex items-center gap-1.5">
              <User className="size-3 shrink-0" />
              Added by {formatName(block.createdByStaff) || block.createdByStaff.email}
            </div>
          )}
          {block.note && (
            <div className="flex items-center gap-1.5">
              <NotebookPen className="size-3 shrink-0" />
              {block.note}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
